import { CommandHandlerFn } from './command-handler'

export interface RemotePlugin {
  name: string
  version: string
  commands: string[]
  code: string
}

export interface PluginLoaderOptions {
  /** 是否允许动态加载（安全开关），默认 false */
  allowDynamic?: boolean
  /** 插件沙箱超时（毫秒），默认 5000 */
  execTimeout?: number
  /** 插件白名单（仅加载这些名称的插件） */
  whitelist?: string[]
}

export interface LoadedPlugin {
  name: string
  version: string
  handlers: Record<string, CommandHandlerFn>
  unload: () => void
}

/**
 * 客户端插件动态加载器
 * 接收服务端推送的插件包并注册 handlers
 */
export class PluginLoader {
  private loaded = new Map<string, LoadedPlugin>()
  private options: Required<PluginLoaderOptions>
  private registerHandler: (cmd: string, fn: CommandHandlerFn) => void
  private removeHandler: (cmd: string) => void

  constructor(
    registerHandler: (cmd: string, fn: CommandHandlerFn) => void,
    removeHandler: (cmd: string) => void,
    options?: PluginLoaderOptions,
  ) {
    this.registerHandler = registerHandler
    this.removeHandler = removeHandler
    this.options = {
      allowDynamic: options?.allowDynamic ?? false,
      execTimeout: options?.execTimeout ?? 5000,
      whitelist: options?.whitelist ?? [],
    }
  }

  /** 加载插件（从服务端推送或本地定义） */
  load(plugin: RemotePlugin): LoadedPlugin {
    if (!this.options.allowDynamic) {
      throw new Error('动态插件加载已禁用')
    }
    if (this.options.whitelist.length > 0 && !this.options.whitelist.includes(plugin.name)) {
      throw new Error(`插件 ${plugin.name} 不在白名单中`)
    }
    if (this.loaded.has(plugin.name)) {
      this.unload(plugin.name)
    }

    const handlers = this.evalPlugin(plugin)
    for (const [cmd, handler] of Object.entries(handlers)) {
      this.registerHandler(cmd, handler)
    }

    const loaded: LoadedPlugin = {
      name: plugin.name,
      version: plugin.version,
      handlers,
      unload: () => this.unload(plugin.name),
    }
    this.loaded.set(plugin.name, loaded)
    return loaded
  }

  /** 加载静态定义的插件（无需 eval，安全） */
  loadStatic(name: string, version: string, handlers: Record<string, CommandHandlerFn>): LoadedPlugin {
    if (this.loaded.has(name)) {
      this.unload(name)
    }
    for (const [cmd, handler] of Object.entries(handlers)) {
      this.registerHandler(cmd, handler)
    }
    const loaded: LoadedPlugin = {
      name,
      version,
      handlers,
      unload: () => this.unload(name),
    }
    this.loaded.set(name, loaded)
    return loaded
  }

  /** 卸载插件 */
  unload(name: string): boolean {
    const plugin = this.loaded.get(name)
    if (!plugin) return false
    for (const cmd of Object.keys(plugin.handlers)) {
      this.removeHandler(cmd)
    }
    this.loaded.delete(name)
    return true
  }

  /** 获取已加载插件列表 */
  listLoaded(): Array<{ name: string; version: string; commands: string[] }> {
    return [...this.loaded.values()].map((p) => ({
      name: p.name,
      version: p.version,
      commands: Object.keys(p.handlers),
    }))
  }

  /** 是否已加载 */
  isLoaded(name: string): boolean {
    return this.loaded.has(name)
  }

  private evalPlugin(plugin: RemotePlugin): Record<string, CommandHandlerFn> {
    try {
      const fn = new Function('exports', plugin.code)
      const exports: Record<string, CommandHandlerFn> = {}
      fn(exports)
      return exports
    } catch (err: any) {
      throw new Error(`插件 ${plugin.name} 加载失败: ${err.message}`)
    }
  }
}
